import { CategoryAxis, ColumnSeries, LineSeries, ValueAxis, XYChart } from "@amcharts/amcharts4/charts";
import { color, create, NumberFormatter } from "@amcharts/amcharts4/core";
import _ from "lodash";
import React, { Component } from "react";
import WebFont from "webfontloader";
import { CCS_ANALYSIS_COLORS, GRAY, RED, WHITE } from "../../../constants";
import LoadingEllipsis from "../../Loading/LoadingEllipsis";
import { disposeChart } from "../helpers/chartHelpers";
import { configureCategoryAxis, configureValueAxis, lineSeriesConfiguration } from "../helpers/configurations";
import { legendConfigurationOCE, oceChartConfiguration } from "../helpers/oceConfigurations";
import { ThemeInt } from "../helpers/types";

interface Props {
  data: Array<Record<string, number | string>>;
  theme: ThemeInt;
  chartId: string;
  isLoading: boolean;
  seriesList: { name: string; field: string; hide: boolean; colour?: string }[];
  percentageChart?: boolean;
  nissanCompetitor?: string;
}

class BrandImageSnapshotChart extends Component<Props> {
  constructor(props: Props) {
    super(props);
  }

  chart!: XYChart;
  chartId = this.props.chartId;

  componentDidMount() {
    WebFont.load({
      custom: {
        families: ["nissan"],
        urls: ["../../../assets/fonts/fonts.css"],
      },
      // @ts-ignore
      active: this.initChart(),
      timeout: 8000,
    });
  }

  componentDidUpdate(prevProps: any) {
    //Handle refreshing the chart when the dataset changes
    if (!_.isEqual(prevProps, this.props)) {
      disposeChart(this.chartId).then((res) => {
        this.initChart();
      });
    }
  }

  componentWillUnmount() {
    disposeChart(this.chartId);
  }

  initChart() {
    const { data, theme, seriesList, percentageChart, nissanCompetitor } = this.props;
    this.chart = create(this.chartId, XYChart);
    oceChartConfiguration(this.chart);
    this.chart.data = data;
    this.chart.numberFormatter.numberFormat = "#a";
    this.chart.paddingRight = 20;
    this.chart.legend = legendConfigurationOCE(theme);
    this.chart.legend.reverseOrder = false;

    // Create Axes
    const categoryAxis = this.chart.yAxes.push(new CategoryAxis());
    configureCategoryAxis(categoryAxis, theme);
    categoryAxis.dataFields.category = "attribute";
    categoryAxis.renderer.grid.template.location = 0;
    categoryAxis.renderer.minGridDistance = 1;
    categoryAxis.renderer.inversed = true;
    // categoryAxis.renderer.labels.template.wrap = true;
    // categoryAxis.renderer.labels.template.maxWidth = 120;

    const valueAxis = this.chart.xAxes.push(new ValueAxis());
    configureValueAxis(valueAxis, theme);
    valueAxis.min = 0;
    valueAxis.renderer.minGridDistance = 100;
    valueAxis.numberFormatter = new NumberFormatter();
    valueAxis.numberFormatter.numberFormat = "#a";
    valueAxis.renderer.labels.template.adapter.add("text", function (text) {
      return text + (percentageChart ? "%" : "");
    });

    const series = this.chart.series.push(new ColumnSeries());
    series.name = "Average";
    series.dataFields.valueX = "score";
    series.dataFields.categoryY = "attribute";
    series.columns.template.tooltipText = `[bold; font-size: var(--regular_font_size);]{categoryY}: {valueX.formatNumber("#,###.0")}%`;
    series.columns.template.fill = color(GRAY);
    series.columns.template.stroke = color(GRAY);
    if (series.tooltip) {
      series.tooltip.autoTextColor = false;
      series.tooltip.label.fill = color(WHITE);
    }

    seriesList.map(({ name, field, hide, colour }, idx) => {
      nissanCompetitor == "all_competitors" ? (series.hidden = hide) : (series.hidden = false);

      const lineSeries = this.chart.series.push(new LineSeries());
      const bullet = lineSeriesConfiguration(lineSeries, {
        valueY: `${field}`,
        name: `${name}`,
      });
      lineSeries.connect = false;

      lineSeries.name = name;
      lineSeries.dataFields.categoryY = "attribute";
      lineSeries.dataFields.valueX = `${field}`;
      lineSeries.fill = color(CCS_ANALYSIS_COLORS[idx]);
      lineSeries.stroke = color(CCS_ANALYSIS_COLORS[idx]);

      if (colour) {
        lineSeries.fill = color(colour);
        lineSeries.stroke = color(colour);
      }

      bullet.adapter.add("tooltipText", (text, target) => {
        const dataItem = target?.dataItem;
        if (!dataItem) return "";

        text = `[bold; font-size: var(--regular_font_size);] ${field} - ${dataItem.categories.categoryY}[/ font-size: var(--regular_font_size);]: {valueX.formatNumber('#,###.0')}`;

        return text;
      });
    });
  }

  render() {
    return (
      <div className={"chartWrapper"}>
        <div id={this.props.chartId} data-test-id={this.props.chartId} className={"graph"} />
        <LoadingEllipsis isLoading={this.props.isLoading} />
      </div>
    );
  }
}

export default BrandImageSnapshotChart;
